import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EcoInputTextComponent } from '@src/app/shared/forms/eco-input-text/eco-input-text.component';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Table, TableModule } from 'primeng/table';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { HeadlessService } from '@src/app/core/services/headless/headless.service';
import { finalize } from 'rxjs';
import { Address } from '@src/app/core/types/address.type';
import { WebformService } from '@src/app/core/services/webform/webform.service';
import { MessageService } from 'primeng/api';
import { WebformRequestChangeAddress } from '@src/app/core/types/webform.type';

@Component({
  selector: 'app-amd-addresses',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    EcoInputTextComponent,
    TranslateModule,
    IconField,
    InputIcon,
  ],
  templateUrl: './amd-addresses.component.html',
  styleUrls: ['./amd-addresses.component.scss'],
})
export class AmdAddressesComponent {
  // Modo del componente: detalle o edición
  mode = signal<'detail' | 'edit'>('detail');
  visible = input<boolean>(false);
  modeOutput = output<'edit' | 'detail'>({ alias: 'mode' });
  isDetail = computed(() => this.mode() === 'detail');
  isEdit = computed(() => this.mode() === 'edit');
  table = viewChild<Table>('table');
  loadingAddresses = signal(false);
  rows = signal(5);

  addresses = signal<Address[]>([]);

  // Fila seleccionada para edición
  selectedIndex = signal<number | null>(null);
  selectedAddress = signal<Address | null>(null);
  savingAddress = signal(false);

  form: FormGroup;

  constructor(
    private headlessService: HeadlessService,
    private webformService: WebformService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {
    effect(() => {
      this.modeOutput.emit(this.mode());
    });

    effect(() => {
      if (this.visible()) {
        this.loadAddresses();
      }
    });
  }

  private loadAddresses() {
    this.loadingAddresses.set(true);
    this.headlessService
      .getDirecciones()
      .pipe(finalize(() => this.loadingAddresses.set(false)))
      .subscribe((response) => {
        this.addresses.set(
          response?.length
            ? response
            : [
                // {
                //   tipo_direccion: 'Dirección Económico Financiera',
                //   provincia: 'Aberdeenshire',
                //   poblacion: 'London',
                //   pais: 'Reino Unido',
                //   codigo_postal: 'NW1 7FB',
                //   direccion: null,
                //   source_id: 0,
                // },
                // {
                //   tipo_direccion: 'Dirección de Envío',
                //   provincia: 'Warwickshire',
                //   poblacion: 'Stratford upon Avon',
                //   pais: 'Reino Unido',
                //   codigo_postal: 'CV37 7GW',
                //   direccion: null,
                //   source_id: 1,
                // },
              ],
        );
        this.form = this.createForm();
      });
  }

  createForm() {
    return new FormGroup({
      tipo_direccion: new FormControl(
        { value: '', disabled: true },
        Validators.required,
      ),
      direccion: new FormControl('', Validators.required),
      provincia: new FormControl('', Validators.required),
      codigo_postal: new FormControl('', Validators.required),
      pais: new FormControl('', Validators.required),
      poblacion: new FormControl('', Validators.required),
    });
  }

  // Acción al pulsar editar en una fila
  editAddress(index: number) {
    const addr = this.addresses()[index];
    this.selectedIndex.set(index);
    this.selectedAddress.set({ ...addr });
    this.form.setValue({
      tipo_direccion: addr.tipo_direccion,
      direccion: addr.direccion,
      provincia: addr.provincia,
      codigo_postal: addr.codigo_postal,
      pais: addr.pais,
      poblacion: addr.poblacion,
    });
    this.mode.set('edit');
  }

  // Volver a la lista (cancelar edición)
  cancelEdit() {
    this.mode.set('detail');
    this.selectedIndex.set(null);
    this.selectedAddress.set(null);
  }

  saveAddress() {
    const value = this.form.getRawValue();
    const webformRequest: Partial<WebformRequestChangeAddress> = {
      direccion: value.direccion ?? '',
      codigo_postal: value.codigo_postal ?? '',
      poblacion: value.poblacion ?? '',
      provincia: value.provincia ?? '',
      pais: value.pais ?? '',
    };

    this.savingAddress.set(true);
    this.webformService
      .updateCompanyAddress(webformRequest)
      .pipe(finalize(() => this.savingAddress.set(false)))
      .subscribe((data) => {
        if (data.sid) {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant(
              'accountManagement.dialog.addresses.toast.edit.summary',
            ),
            detail: this.translate.instant(
              'accountManagement.dialog.addresses.toast.edit.detail',
            ),
          });

          this.loadAddresses();
          this.mode.set('detail');
        }
      });
  }

  onGlobalFilter(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const value = input?.value ?? '';
    this.table()?.filterGlobal(value, 'contains');
  }
}
